'use strict';

/**
 * Discovery "project tools" data source
 * -------------------------------------
 * {@link useDiscoveryPanelEntries} supplies discovery rows (product types, titles, MUI icons, paths).
 * All surfaces that show **which** project tools exist should use {@link buildProjectTools} in
 * `features/projectTools/projectToolsBuilder.js` with these entries and `hasDiscoveryPanels`, then map with
 * {@link projectToolsToLandingCards}, {@link projectToolsToSidebarItems}, or {@link projectToolsToExploreItems}.
 *
 * Use {@link getNavigableDiscoveryEntries} only inside the builder (and tests): it is the single rule for
 * linkable discovery rows (must have a `path`). Use `hasDiscoveryPanels` to treat a project as discovery-enabled
 * at the API level even before paths resolve.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
	Science,
	LocalHospital,
	Favorite,
	Dashboard,
	Biotech,
} from '@mui/icons-material';
import { useProjectsQuery as useDiscoveryQuery } from '@/redux/services/discovery/projects.api';
import { fetchSampleSets } from '@/discovery-panel/api';
import {
	DiscoveryPanelMap,
	DISCOVERY_PREFIX,
	BIOSTRESS,
	MICROBIOME,
	LIVERFIBROSIS,
	KIDNEYFUNCTION,
} from '@/enums/Enums';
import { Microscope, TestTube2, Activity, HeartPulse, LayoutDashboard } from 'lucide-react';

/** Lucide icons for project sidebar (metabux NavItem); aligned to discovery product types. */
export function getDiscoveryPanelNavLucideIcon(productType) {
	const iconMap = {
		[BIOSTRESS]: Microscope,
		[MICROBIOME]: TestTube2,
		[LIVERFIBROSIS]: Activity,
		[KIDNEYFUNCTION]: HeartPulse,
	};
	return iconMap[productType] || LayoutDashboard;
}

/** Body copy for ProjectHome Explore tiles (discovery mode); keep in one place. */
export const DISCOVERY_PANEL_EXPLORE_BODY_TEXT = 'Open this discovery panel workspace.';

/** Subset with a resolved deep link — use for sidebar, Explore, and landing discovery cards. */
export function getNavigableDiscoveryEntries(entries) {
	return entries.filter((e) => e.path);
}

/** Same icon mapping as molecular landing “Project Tools” discovery cards. */
export function getDiscoveryPanelProductTypeIcon(productType) {
	const iconMap = {
		[BIOSTRESS]: Science,
		[MICROBIOME]: Biotech,
		[LIVERFIBROSIS]: LocalHospital,
		[KIDNEYFUNCTION]: Favorite,
	};
	const IconComponent = iconMap[productType] || Dashboard;
	return <IconComponent />;
}

/**
 * Discovery panels for a project: product types, sample-set ids, and navigation paths
 * (aligned with molecular home “Project Tools” discovery cards).
 */
export function useDiscoveryPanelEntries(projectId) {
	const { data: discoveryPanelsData } = useDiscoveryQuery(projectId ? [projectId] : [], {
		skip: !projectId,
	});

	const productTypes = useMemo(() => {
		if (!discoveryPanelsData || !Array.isArray(discoveryPanelsData) || !projectId) {
			return [];
		}
		const projectPanels = discoveryPanelsData.filter((panel) => panel.projectId === projectId);
		const uniqueProductTypes = [
			...new Set(
				projectPanels.map((panel) => {
					const productType = panel.productType || '';
					return productType.startsWith(DISCOVERY_PREFIX)
						? productType.substring(DISCOVERY_PREFIX.length)
						: productType;
				}),
			),
		];
		return uniqueProductTypes.filter(Boolean);
	}, [discoveryPanelsData, projectId]);

	const [sampleSetIdsMap, setSampleSetIdsMap] = useState({});

	useEffect(() => {
		const fetchSampleSetIds = async () => {
			if (!projectId || productTypes.length === 0) {
				setSampleSetIdsMap({});
				return;
			}
			const newMap = {};
			for (const productType of productTypes) {
				try {
					const sampleSetIds = await fetchSampleSets(projectId, productType);
					newMap[productType] = Array.isArray(sampleSetIds) ? sampleSetIds : [];
				} catch (error) {
					console.error(`Error fetching sampleSetIds for ${productType}:`, error);
					newMap[productType] = [];
				}
			}
			setSampleSetIdsMap(newMap);
		};
		fetchSampleSetIds();
	}, [projectId, productTypes]);

	const entries = useMemo(() => {
		if (productTypes.length === 0) {
			return [];
		}
		return productTypes.map((productType) => {
			const sampleSetIds = sampleSetIdsMap[productType] || [];
			const firstSampleSetId = sampleSetIds.length > 0 ? sampleSetIds[0] : null;
			const displayName = DiscoveryPanelMap.ui[productType] || productType;
			const title = `${displayName} Discovery Panel`;
			const path =
				firstSampleSetId && projectId
					? `/discovery-panels/${projectId}/${productType}/${firstSampleSetId}`
					: null;
			return {
				id: `discovery-${productType}`,
				productType,
				title,
				icon: getDiscoveryPanelProductTypeIcon(productType),
				path,
			};
		});
	}, [productTypes, sampleSetIdsMap, projectId]);

	return {
		entries,
		hasDiscoveryPanels: entries.length > 0,
		productTypes,
	};
}
